import { useState, useEffect } from 'react';
import Loading from '@/components/Loading';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import useFetchCompany from '../api/useFetchCompany';
import { TAB_ITEMS } from '../constants';
import CompaniesProfileTab from '../components/CompaniesProfileTab';
import CompaniesProfileCard from '../components/CompaniesProfileCard';
import CompaniesJobsCard from '../components/CompaniesJobsCard';
import CompaniesTimelineCard from '../components/CompaniesTimelineCard';
import CompaniesMatchingCard from '../components/CompaniesMatchingCard';
import clsx from 'clsx';
import CompaniesJobPostEndpointsCard from '../components/CompaniesJobPostEndpointsCard';
import useFetchActiveUsers from '../../api/useFetchActiveUsers';
import useFetchCompaniesCustomFields from '../api/useFetchCompaniesCustomFields';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import useFetchIam from '@/features/agent/api/useFetchIam';

type Props = {
  id: string;
};

export default function CompaniesShowTemplate({ id }: Props) {
  const { data: company, refetch: companyRefetch, isLoading } = useFetchCompany({ id: Number(id) });
  const { data: activeUsers } = useFetchActiveUsers();
  const { data: customFields } = useFetchCompaniesCustomFields();
  const { data: iam } = useFetchIam();
  const isJobOnlyPlan = iam?.plan_type === 'job_only';
  useSetBreadcrumbTitle(company?.name);
  useDocumentTitle(company?.name || '企業管理');
  const isInitialLoading = isLoading && !company;

  const getTabFromUrl = (): (typeof TAB_ITEMS)[number]['value'] => {
    if (typeof window === 'undefined') return 'profile';

    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');

    return tabParam && TAB_ITEMS.some((item) => item.value === tabParam)
      ? (tabParam as (typeof TAB_ITEMS)[number]['value'])
      : 'profile';
  };

  const [tab, setTab] = useState<(typeof TAB_ITEMS)[number]['value']>(getTabFromUrl());

  const handleTabChange = (newTab: (typeof TAB_ITEMS)[number]['value']) => {
    setTab(newTab);

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', newTab);
      window.history.replaceState({}, '', url.toString());
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setTab(getTabFromUrl());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, []);

  if (isInitialLoading) {
    return (
      <div className="flex h-[calc(100vh-60px)] w-full flex-col items-center justify-center gap-2">
        <Loading size="small" />
        <p className="text-sm text-placeholder">企業情報を読み込み中です…</p>
      </div>
    );
  }

  return (
    <div className="hidden-scrollbar flex h-[calc(100vh-60px)] flex-col">
      <div className="sticky z-10 bg-white pt-md">
        <CompaniesProfileTab
          companyId={Number(id)}
          tab={tab}
          setTab={handleTabChange}
          companyName={company?.name || ''}
          address={company?.company_information?.address || ''}
          presidentName={company?.company_information?.president_name || ''}
          providedJobPostsCount={company?.provided_job_posts_count ?? 0}
          matchedCount={company?.matched_count ?? 0}
          suggestedJobPostsCount={company?.suggested_job_posts_count ?? 0}
          availableProvidedJobPostsCount={company?.available_provided_job_posts_count ?? 0}
          tags={
            company?.tags?.map((tag) => ({
              id: tag.id,
              name: tag.name,
              colorHex: tag.color_hex,
            })) || []
          }
          organizationUserIds={company?.organization_users?.map((u) => u.id) ?? []}
          activeUsers={activeUsers?.records || []}
          currentUserId={iam?.current_user?.id}
          files={
            company?.files
              ?.filter((file) => file.id !== null)
              .map((file) => ({
                id: file.id!,
                filename: file.filename || '',
                url: file.url || '',
              })) || []
          }
          companyRefetch={companyRefetch}
        />
      </div>
      <section
        className={clsx(
          'hidden-scrollbar w-full flex-1 overflow-auto p-6',
          tab === 'matching' && !isJobOnlyPlan && 'p-0!',
          tab === 'timeline' && 'p-0!',
          tab === 'jobs' && 'p-0!'
        )}
      >
        {tab === 'profile' && (
          <CompaniesProfileCard
            companyId={Number(id)}
            companyName={company?.name || ''}
            customData={company?.custom_data || []}
            organizationUserIds={company?.organization_users?.map((u) => u.id) ?? []}
            companyInformation={company?.company_information || null}
            suggestionInfo={company?.suggestion_info || null}
            companyContract={company?.company_contract || null}
            customFields={customFields?.records || []}
            companyRefetch={companyRefetch}
          />
        )}
        {tab === 'timeline' && (
          <CompaniesTimelineCard companyId={Number(id)} currentUserId={iam?.current_user?.id} />
        )}
        {tab === 'jobs' && <CompaniesJobsCard companyId={Number(id)} />}
        {tab === 'suggestion' && (
          <CompaniesJobPostEndpointsCard
            isLoading={isLoading}
            jobPostEndpoints={company?.job_post_endpoints ?? []}
            companyRefetch={companyRefetch}
          />
        )}
        {tab === 'matching' && !isJobOnlyPlan && <CompaniesMatchingCard companyId={id} />}
      </section>
    </div>
  );
}
